/**
 * BatchInvestmentRow — one collapsible card representing a single investment
 * within the batch-add flow. Reuses the same field components as AddInvestmentModal
 * so the data shape and validation rules are identical.
 *
 * The investment type is set at the parent level (shared across all rows), so
 * `hideType` is passed to InvestmentBasicFields.
 */
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { OwnershipTypeSelector } from '../OwnershipTypeSelector';
import InvestmentBasicFields from './InvestmentBasicFields';
import InvestmentFormMiddle from './InvestmentFormMiddle';
import TypeSpecificFields from './TypeSpecificFields';

const BatchInvestmentRow = ({
  index, rowData, onFieldChange, onRemove, canRemove,
  config, setConfig, entities, relatedAssets, errors = {}, isIncomplete = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const title = rowData.name?.trim() || `Investment #${index + 1}`;
  const summary = rowData.amount ? `${rowData.currency || ''} ${rowData.amount}` : 'Not filled';
  const borderClass = errors._row
    ? 'border-rose-300 ring-1 ring-rose-200'
    : isIncomplete ? 'border-amber-300' : 'border-slate-200';

  return (
    <div
      className={`border rounded-lg bg-white shadow-sm ${borderClass}`}
      data-testid={`batch-row-${index}`}
    >
      <div className="flex items-center justify-between gap-2 p-3 border-b border-slate-100 bg-slate-50/60 rounded-t-lg">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-emerald-700 min-w-0"
          data-testid={`batch-row-toggle-${index}`}
        >
          {collapsed ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronUp className="w-4 h-4 shrink-0" />}
          <span className="truncate">{title}</span>
          {collapsed && <span className="text-xs text-slate-500 font-normal ml-2">{summary}</span>}
        </button>
        {canRemove && (
          <Button
            type="button" variant="ghost" size="sm"
            onClick={() => onRemove(index)}
            className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            data-testid={`batch-row-remove-${index}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      {!collapsed && (
        <div className="p-4 space-y-4">
          {errors._row && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid={`batch-row-errors-${index}`}>
              {errors._row.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          <InvestmentBasicFields
            formData={rowData} onFieldChange={onFieldChange} config={config}
            errors={errors} hideType
          />
          <OwnershipTypeSelector
            ownerType={rowData.owner_type} entityId={rowData.entity_id}
            entities={entities} relatedAssets={relatedAssets}
            onOwnerTypeChange={(t) => { onFieldChange('owner_type', t); onFieldChange('entity_id', ''); }}
            onEntityChange={(id) => onFieldChange('entity_id', id)}
          />
          <InvestmentFormMiddle
            formData={rowData} onFieldChange={onFieldChange}
            config={config} setConfig={setConfig} errors={errors}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TypeSpecificFields
              formData={rowData} onFieldChange={onFieldChange}
              config={config} setConfig={setConfig} errors={errors}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`pd-${index}`} className="portfolio-input-label">Purchase Date</Label>
              <Input id={`pd-${index}`} type="date" value={rowData.purchase_date || ''}
                onChange={(e) => onFieldChange('purchase_date', e.target.value)}
                className="portfolio-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`notes-${index}`} className="portfolio-input-label">Notes</Label>
              <Textarea id={`notes-${index}`} placeholder="Any additional notes..."
                value={rowData.notes || ''} onChange={(e) => onFieldChange('notes', e.target.value)}
                className="portfolio-input min-h-[60px] resize-none" rows={2} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchInvestmentRow;
